import { GenericHTMLProps } from 'components';
import { A11y } from 'domain/utils/a11y.utils';
import React from 'react';

interface UseA11y extends GenericHTMLProps {
	defaultTitle?: string;
	defaultAriaLabel?: string;
	ariaLabelPrefix?: string;
}
/**
 * Attempts to infer the title and label of an element.
 * If the title and aria-label properties have been provided,
 * no operation is performed. Otherwise, an attempt is
 * made to use the content of the element.
 * @param param0 Standard HTML element props
 * @returns `[title: string, ariaLabel: string]`
 */
export const useA11y = ({
	children,
	'aria-label': ariaLabel,
	title,
	ariaLabelPrefix,
	defaultAriaLabel,
	defaultTitle,
}: UseA11y) => {
	const _title = React.useMemo(() => {
		return title ?? A11y.getTitle(children, defaultTitle);
	}, [defaultTitle, children, title]);

	const _ariaLabel = React.useMemo(() => {
		const cl = A11y.getAriaLabel(children, ariaLabelPrefix, defaultAriaLabel);
		return ariaLabel ?? cl;
	}, [children, ariaLabel, ariaLabelPrefix, defaultAriaLabel]);

	return React.useMemo(() => [_title, _ariaLabel], [_title, _ariaLabel]);
};
